import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Section variant styles using Class Variance Authority (CVA)
 *
 * Defines visual variants, gap, padding, and rounded corner options for the Section component.
 * All styles use design tokens from the HEN Design System.
 *
 * ## Variants
 * - default: No background styling
 * - light: Light gray background (bg-neutral-50)
 * - dark: Dark background (bg-neutral-900) with white text
 * - white: Plain white background (bg-white)
 * - white-outline: White background with border (bg-white border-neutral-200)
 * - elevated-footer: Light elevated footer surface (bg, top border, shadow)
 *
 * ## Gap Options (internal spacing between children)
 * - xxs through 3xl, plus max (80px) matching design tokens
 *
 * ## Padding Options
 * Option names match the spacing token they apply, mirroring Card.
 * - none: No padding
 * - xs, sm, md, lg: that spacing token on all sides (p-xs, p-sm, p-md, p-lg)
 * - section: Section-specific padding tokens (px-section-sides py-section-top-bottom)
 *
 * ## Min Height Options
 * - screen: Full viewport height (min-h-dvh)
 *
 * ## Position Options
 * - sticky-bottom: Sticky-positioned at the bottom with elevated z-index
 */
export const sectionVariants = cva('flex flex-col', {
  variants: {
    variant: {
      default: '',
      light: 'bg-neutral-50',
      dark: 'bg-neutral-900 text-white',
      white: 'bg-white',
      'white-outline': 'bg-white border border-neutral-200',
      'white-border-top': 'bg-neutral-white border-t border-neutral-200',
      'elevated-footer': 'bg-neutral-50 border-t border-neutral-200 shadow-xl',
    },
    gap: {
      min: 'gap-min',
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
      xl: 'gap-xl',
      '2xl': 'gap-2xl',
      '3xl': 'gap-3xl',
      max: 'gap-max',
    },
    padding: {
      none: '',
      xs: 'p-xs',
      sm: 'p-sm',
      md: 'p-md',
      lg: 'p-lg',
      section: 'px-section-sides py-section-top-bottom',
      'page-bar': 'px-page-sides py-md',
    },
    rounded: {
      true: 'rounded-xl',
      false: '',
      none: '',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
    },
    minHeight: {
      screen: 'min-h-dvh',
    },
    position: {
      'sticky-bottom': 'sticky bottom-0 z-10',
    },
  },
  defaultVariants: {
    variant: 'default',
    gap: '3xl',
    padding: 'section',
    rounded: false,
  },
});

export type SectionVariantProps = VariantProps<typeof sectionVariants>;
